import { EMemberSnapshotDataDomains, type TLinkedinConnection, type TLinkedInMapperData } from '#shared/types/LinkedIn'
import { LINKEDIN_OAUTH_CALLBACK_PATH, LINKEDIN_OAUTH_RETURN_TO_STORAGE_KEY } from '#shared/constants/linkedIn'

type LinkedInProfileResponse = {
    profile: TLinkedInMapperData['profile']
    domains: EMemberSnapshotDataDomains[]
}

const inFlightRefreshRequests = new Map<string, Promise<LinkedInProfileResponse>>()

function createRefreshRequestKey(
    domains: EMemberSnapshotDataDomains[],
    missing: EMemberSnapshotDataDomains[]
): string {
    const sortedDomains = [...domains].sort().join(',')
    const sortedMissing = [...missing].sort().join(',')
    return `domains:${sortedDomains}|missing:${sortedMissing}`
}

function normalizeLinkedInReturnTo(path: string | null | undefined): string | null {
    if (!path || !path.startsWith('/')) return null

    // Compare on the pathname only — the stored value keeps its query string
    const [pathname] = path.split('?')

    if (pathname === '/profile/edit') {
        return LINKEDIN_OAUTH_CALLBACK_PATH
    }

    return path
}

/**
 * Composable for LinkedIn functionality
 *
 * Uses Cloud Functions API ($api) for all LinkedIn operations.
 * The $api plugin automatically handles Firebase authentication.
 */
export function useLinkedin() {
    const { $api } = useNuxtApp()
    const route = useRoute()

    const storeLinkedInReturnTo = () => {
        if (!import.meta.client) return

        // Keep the query string: it carries page state (wizard step, tab, invite code)
        // that used to be dropped, so the user came back to a differently-rendered page
        const currentPath = route.fullPath || route.path || '/'
        const normalizedPath = normalizeLinkedInReturnTo(currentPath)

        if (normalizedPath) {
            localStorage.setItem(LINKEDIN_OAUTH_RETURN_TO_STORAGE_KEY, normalizedPath)
        }
    }

    const consumeLinkedInReturnTo = (): string | null => {
        if (!import.meta.client) return null

        const returnTo = normalizeLinkedInReturnTo(localStorage.getItem(LINKEDIN_OAUTH_RETURN_TO_STORAGE_KEY))
        localStorage.removeItem(LINKEDIN_OAUTH_RETURN_TO_STORAGE_KEY)
        return returnTo
    }

    /**
     * Get authorization link for LinkedIn OAuth
     */
    const getAuthorizationLink = async (
        domains: EMemberSnapshotDataDomains[],
        redirect_uri: string
    ): Promise<{ url: string }> => {
        storeLinkedInReturnTo()

        const response = await $api.get('/linkedin/auth/url', {
            params: { redirect_uri, domains },
            timeout: 5000
        })
        return response.data
    }

    /**
     * Get connection data for LinkedIn
     */
    const getConnectionData = async (): Promise<Pick<TLinkedinConnection, 'domains' | 'syncFields'>> => {
        const response = await $api.get('/linkedin/auth/data', {
            // 15s (not 5s): a cold Cloud Function cold-start can exceed 5s and abort,
            // which would render a genuinely-connected user as "not connected".
            timeout: 15000
        })
        return response.data
    }

    /**
     * Update LinkedIn connection data
     */
    const updateConnectionData = async (
        data: Pick<TLinkedinConnection, 'syncFields'> & { syncStage: TLinkedinConnection['syncStages'][number] }
    ): Promise<Pick<TLinkedinConnection, 'domains' | 'syncFields'>> => {
        const response = await $api.put('/linkedin/auth/data', data, {
            timeout: 5000
        })
        return response.data
    }

    /**
     * Delete LinkedIn connection data
     */
    const deleteConnectionData = async (): Promise<true> => {
        const response = await $api.delete('/linkedin/auth/data', {
            timeout: 5000
        })
        return response.data
    }

    /**
     * Get LinkedIn profile data (also handles OAuth callback)
     */
    const getProfileData = async (
        redirect_uri?: string,
        code?: string,
        state?: string
    ): Promise<LinkedInProfileResponse> => {
        const response = await $api.get('/linkedin/profile', {
            params: { redirect_uri, code, state },
            timeout: 120000 // 2 min timeout - OAuth callback + LinkedIn API calls can be slow
        })
        return response.data
    }

    /**
     * Refresh LinkedIn profile data for missing domains
     */
    const refreshProfileData = async (
        domains: EMemberSnapshotDataDomains[],
        missing: EMemberSnapshotDataDomains[]
    ): Promise<LinkedInProfileResponse> => {
        const key = createRefreshRequestKey(domains, missing)
        const existingRequest = inFlightRefreshRequests.get(key)
        if (existingRequest) {
            return existingRequest
        }

        const requestPromise = (async () => {
            const response = await $api.get('/linkedin/profile/refresh', {
                params: { domains, missing },
                timeout: 30000 // Longer timeout - LinkedIn API calls
            })
            return response.data as LinkedInProfileResponse
        })()

        inFlightRefreshRequests.set(key, requestPromise)

        try {
            return await requestPromise
        } finally {
            inFlightRefreshRequests.delete(key)
        }
    }

    return {
        getAuthorizationLink,
        getConnectionData,
        updateConnectionData,
        deleteConnectionData,
        getProfileData,
        refreshProfileData,
        consumeLinkedInReturnTo
    }
}
